import React, { useState, useRef, useEffect, type FC, type PropsWithChildren } from 'react';

import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';

import Box from '../box/box';
import Icon from '../icon/icon';
import LoadingIndicator from '../loading-indicator/loading-indicator';
import MarkdownParser from '../markdown-parser/markdown-parser';

export interface ThumbnailViewerProps {
  /** An image to be placed above to the text inside the viewer. */
  imageSrc: string;
  /** The text displayed underneath the thumbnail viewer */
  description: string;
}

const ThumbnailViewer: FC<PropsWithChildren<ThumbnailViewerProps>> = ({ imageSrc, description }) => {
  const [hasLoaded, setHasLoaded] = useState(false);

  const ref = useRef<HTMLImageElement>(null);
  const onLoad = () => setHasLoaded(true);

  useEffect(() => {
    if (ref.current && ref.current.complete) {
      onLoad();
    }
  });

  const Controls = () => {
    const { zoomIn, zoomOut } = useControls();

    return (
      <Box
        className="tools"
        css={theme => ({
          display: 'flex',
          gap: theme.space.s8,
          margin: theme.space.s8,
          justifyContent: 'flex-end',
          position: 'absolute',
          right: 0,
          bottom: 0,
        })}
      >
        <Box onClick={() => zoomIn()}>
          <Icon
            color="coolGrey.100"
            css={theme => ({
              backgroundColor: theme.colors.white,
              height: theme.space.s48,
              width: theme.space.s48,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '100%',
              border: `1px solid ${theme.colors.coolGrey[100]}`,
              svg: {
                height: theme.space.s24,
              },
              [theme.breakpoints.bp500.min]: {
                height: theme.space.s64,
                width: theme.space.s64,
                svg: {
                  height: theme.space.s32,
                },
              },
            })}
            sizing="responsive"
            type="ZoomIn"
          />
        </Box>
        <Box onClick={() => zoomOut()}>
          <Icon
            color="coolGrey.100"
            css={theme => ({
              backgroundColor: theme.colors.white,
              height: theme.space.s48,
              width: theme.space.s48,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '100%',
              border: `1px solid ${theme.colors.coolGrey[100]}`,
              svg: {
                height: theme.space.s24,
              },
              [theme.breakpoints.bp500.min]: {
                height: theme.space.s64,
                width: theme.space.s64,
                svg: {
                  height: theme.space.s32,
                },
              },
            })}
            sizing="responsive"
            type="ZoomOut"
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      css={theme => ({
        marginBottom: theme.space.s64,
        width: '980px',
        maxWidth: '100%',
      })}
    >
      <Box
        css={theme => ({
          borderRadius: `${theme.radii.r12}px ${theme.radii.r12}px 0 0`,
          borderTop: `solid 1px ${theme.colors.black}`,
          borderLeft: `solid 1px ${theme.colors.black}`,
          borderRight: `solid 1px ${theme.colors.black}`,
          overflow: 'hidden',
          height: '500px',
          position: 'relative',
        })}
      >
        <TransformWrapper initialScale={1}>
          <Box css={{ position: 'relative' }}>
            <TransformComponent wrapperStyle={{ height: '500px', width: '100%' }}>
              <img ref={ref} alt={description} onLoad={onLoad} src={imageSrc} />
              {!hasLoaded && <LoadingIndicator />}
            </TransformComponent>
            <Controls />
          </Box>
        </TransformWrapper>
      </Box>
      {description && (
        <Box
          css={theme => ({
            borderRadius: `0 0 ${theme.radii.r12}px ${theme.radii.r12}px`,
            border: `solid 1px ${theme.colors.black}`,
            padding: `${theme.space.s16}px ${theme.space.s32}px`,
            backgroundColor: theme.colors.coolGrey[5],
            'p, li': {
              fontSize: theme.fontSizes.fs16,
            },
          })}
          id="thumbnail-description"
        >
          <MarkdownParser>{description}</MarkdownParser>
        </Box>
      )}
    </Box>
  );
};

export default ThumbnailViewer;
